import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Edit, Trash2, EyeOff, Eye, AlertTriangle } from 'lucide-react';

const Barbers = () => {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const [barbers, setBarbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingBarber, setEditingBarber] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    specialty: ''
  });

  useEffect(() => {
    fetchBarbers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBarbers = async () => {
    try {
      // المدير يرى جميع الحلاقين، الآخرون يرون فقط غير المستثنيين
      const endpoint = isAdmin() ? '/barbers/all' : '/barbers';
      const res = await api.get(endpoint);
      setBarbers(res.data);
    } catch (error) {
      toast.error('فشل تحميل الحلاقين');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBarber) {
        await api.put(`/barbers/${editingBarber.id}`, formData);
        toast.success('تم تحديث الحلاق بنجاح');
      } else {
        await api.post('/barbers', formData);
        toast.success('تم إضافة الحلاق بنجاح');
      }
      setOpen(false);
      setEditingBarber(null);
      setFormData({ name: '', phone: '', specialty: '' });
      fetchBarbers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشلت العملية');
    }
  };

  const handleEdit = (barber) => {
    setEditingBarber(barber);
    setFormData({
      name: barber.name,
      phone: barber.phone || '',
      specialty: barber.specialty || ''
    });
    setOpen(true);
  };

  const handleDelete = async (barberId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا الحلاق؟')) return;
    try {
      await api.delete(`/barbers/${barberId}`);
      toast.success('تم حذف الحلاق بنجاح');
      fetchBarbers();
    } catch (error) {
      toast.error('فشل حذف الحلاق');
    }
  };

  // تبديل حالة استثناء الحلاق
  const handleToggleExclude = async (barberId, currentExcluded) => {
    const action = currentExcluded ? 'إلغاء استثناء' : 'استثناء';
    const confirmMessage = currentExcluded 
      ? 'هل تريد إلغاء استثناء هذا الحلاق؟ سيظهر مرة أخرى في التقارير والتقييمات.'
      : 'هل تريد استثناء هذا الحلاق؟ لن يظهر في التقارير والتقييمات والإحصائيات.';
    
    if (!window.confirm(confirmMessage)) return;
    
    try {
      const res = await api.put(`/barbers/${barberId}/toggle-exclude`);
      toast.success(res.data.message);
      fetchBarbers();
    } catch (error) {
      toast.error(error.response?.data?.detail || `فشل ${action} الحلاق`);
    }
  };

  // تبديل حالة الإظهار للعملاء
  const handleToggleVisibility = async (barberId, currentVisible) => {
    const action = currentVisible ? 'إخفاء' : 'إظهار';
    const confirmMessage = currentVisible 
      ? 'هل تريد إخفاء هذا الحلاق من الصفحة الرئيسية للعملاء؟'
      : 'هل تريد إظهار هذا الحلاق في الصفحة الرئيسية للعملاء؟';
    
    if (!window.confirm(confirmMessage)) return;
    
    try {
      const res = await api.put(`/barbers/${barberId}/toggle-visibility`);
      toast.success(res.data.message);
      fetchBarbers();
    } catch (error) {
      toast.error(error.response?.data?.detail || `فشل ${action} الحلاق للعملاء`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  // فصل الحلاقين إلى نشطين ومستثنيين
  const activeBarbers = barbers.filter(b => !b.is_excluded);
  const excludedBarbers = barbers.filter(b => b.is_excluded);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">{t('barbers')}</h1>
          <p className="text-muted-foreground">إدارة الحلاقين</p>
        </div>
        {isAdmin() && (
          <Dialog open={open} onOpenChange={(open) => {
            setOpen(open);
            if (!open) {
              setEditingBarber(null);
              setFormData({ name: '', phone: '', specialty: '' });
            }
          }}>
            <DialogTrigger asChild>
              <Button data-testid="add-barber-btn" className="bg-primary text-primary-foreground">
                <Plus className="w-4 h-4 me-2" />
                {t('addBarber')}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-white/10">
              <DialogHeader>
                <DialogTitle>{editingBarber ? t('edit') : t('addBarber')}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">{t('name')}</label>
                  <Input
                    data-testid="barber-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="bg-secondary/50 border-white/10"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">رقم الهاتف</label>
                  <Input
                    data-testid="barber-phone-input"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="bg-secondary/50 border-white/10"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">التخصص</label>
                  <Input
                    data-testid="barber-specialty-input"
                    value={formData.specialty}
                    onChange={(e) => setFormData({ ...formData, specialty: e.target.value })}
                    className="bg-secondary/50 border-white/10"
                  />
                </div>
                <Button data-testid="submit-barber-btn" type="submit" className="w-full bg-primary text-primary-foreground">
                  {t('save')}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* الحلاقين النشطين */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {activeBarbers.map((barber) => (
          <div key={barber.id} data-testid={`barber-card-${barber.id}`} className="bg-card border border-white/5 rounded-xl shadow-2xl p-6 hover:border-primary/20 transition-colors">
            <div className="flex items-start justify-between mb-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                <span className="text-2xl font-bold text-primary">
                  {barber.name.charAt(0)}
                </span>
              </div>
              {isAdmin() && (
                <div className="flex gap-2">
                  {/* زر تبديل الإظهار للعملاء */}
                  <Button
                    data-testid={`toggle-visibility-${barber.id}`}
                    onClick={() => handleToggleVisibility(barber.id, barber.is_visible_to_public !== false)}
                    variant="ghost"
                    size="sm"
                    className={barber.is_visible_to_public !== false ? "text-green-500 hover:bg-green-500/10" : "text-gray-500 hover:bg-gray-500/10"}
                    title={barber.is_visible_to_public !== false ? "مرئي للعملاء - اضغط للإخفاء" : "مخفي عن العملاء - اضغط للإظهار"}
                  >
                    {barber.is_visible_to_public !== false ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </Button>
                  <Button
                    data-testid={`exclude-barber-${barber.id}`}
                    onClick={() => handleToggleExclude(barber.id, barber.is_excluded)}
                    variant="ghost"
                    size="sm"
                    className="text-orange-500 hover:bg-orange-500/10"
                    title="استثناء من التقارير"
                  >
                    <AlertTriangle className="w-4 h-4" />
                  </Button>
                  <Button
                    data-testid={`edit-barber-${barber.id}`}
                    onClick={() => handleEdit(barber)}
                    variant="ghost"
                    size="sm"
                    className="text-primary hover:bg-primary/10"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    data-testid={`delete-barber-${barber.id}`}
                    onClick={() => handleDelete(barber.id)}
                    variant="ghost"
                    size="sm"
                    className="text-red-500 hover:bg-red-500/10"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
            <h3 className="text-xl font-bold mb-2">{barber.name}</h3>
            {barber.phone && (
              <p className="text-sm text-muted-foreground mb-1">📱 {barber.phone}</p>
            )}
            {barber.specialty && (
              <p className="text-sm text-muted-foreground">✂️ {barber.specialty}</p>
            )}
            {/* مؤشر الإظهار للعملاء */}
            {isAdmin() && (
              <div className={`mt-3 text-xs px-2 py-1 rounded-full inline-flex items-center gap-1 ${
                barber.is_visible_to_public !== false 
                  ? 'bg-green-500/10 text-green-500' 
                  : 'bg-gray-500/10 text-gray-400'
              }`}>
                {barber.is_visible_to_public !== false ? (
                  <><Eye className="w-3 h-3" /> مرئي للعملاء</>
                ) : (
                  <><EyeOff className="w-3 h-3" /> مخفي عن العملاء</>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* الحلاقين المستثنيين - للمدير فقط */}
      {isAdmin() && excludedBarbers.length > 0 && (
        <div className="mt-8">
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="w-5 h-5 text-orange-500" />
            <h2 className="text-xl font-bold text-orange-500">الحلاقين المستثنيين ({excludedBarbers.length})</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            هؤلاء الحلاقين لا يظهرون في التقارير والتقييمات والإحصائيات العامة
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {excludedBarbers.map((barber) => (
              <div 
                key={barber.id} 
                data-testid={`excluded-barber-card-${barber.id}`} 
                className="bg-card border border-orange-500/30 rounded-xl shadow-2xl p-6 opacity-70"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-16 h-16 bg-orange-500/10 rounded-full flex items-center justify-center relative">
                    <span className="text-2xl font-bold text-orange-500">
                      {barber.name.charAt(0)}
                    </span>
                    <EyeOff className="w-4 h-4 text-orange-500 absolute -bottom-1 -right-1" />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      data-testid={`include-barber-${barber.id}`}
                      onClick={() => handleToggleExclude(barber.id, barber.is_excluded)}
                      variant="ghost"
                      size="sm"
                      className="text-green-500 hover:bg-green-500/10"
                      title="إلغاء الاستثناء"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      data-testid={`edit-excluded-barber-${barber.id}`}
                      onClick={() => handleEdit(barber)}
                      variant="ghost"
                      size="sm"
                      className="text-primary hover:bg-primary/10"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      data-testid={`delete-excluded-barber-${barber.id}`}
                      onClick={() => handleDelete(barber.id)}
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-xl font-bold text-orange-400">{barber.name}</h3>
                  <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30 text-xs">
                    مستثنى
                  </Badge>
                </div>
                {barber.phone && (
                  <p className="text-sm text-muted-foreground mb-1">📱 {barber.phone}</p>
                )}
                {barber.specialty && (
                  <p className="text-sm text-muted-foreground">✂️ {barber.specialty}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Barbers;
